import { objectToQueryString } from "../../shared/utils";
import { chartTooltipHtml } from "../chartTooltipHtml";
import { formatTime } from "@/lib/formatters";
import { fetchWithPolling } from "@/lib/jsonRequest";

export function snapshotResultsLineChart(args) {
  const { endpoint, name, filters, chartIndex, chartData, unit } = args;

  let chart = null;

  return {
    displayable: true,
    chartData,
    chartSql: null,
    loading: false,
    variant: null,
    showQueryModal: false,
    sqlModalChartTitle: "",
    rawSql: null,
    unit,
    setVariant(variant) {
      this.variant = variant;
      this.reloadChart();
    },
    formatForNewApiData(data) {
      if (!data || !data.time_series) return data;

      // Format dates to create labels (e.g., "Apr 29 - May 5")
      const formattedData = data.time_series.values.map((item) => {
        const date = new Date(item.date);
        const endDate = new Date(date);
        endDate.setDate(date.getDate() + 6); // End of week (6 days later)

        const startMonth = date.toLocaleString("en-US", { month: "short" });
        const startDay = date.getDate();
        const endMonth = endDate.toLocaleString("en-US", { month: "short" });
        const endDay = endDate.getDate();

        return {
          date: item.date.split("T")[0],
          value: item.value,
          label: `${startMonth} ${startDay} - ${endMonth} ${endDay}`,
          unit: "",
          color: "url(#barChartGradient)",
        };
      });

      // Find start and end dates
      const startDate = data.time_series.dates[0];
      const endDate = data.time_series.dates[data.time_series.dates.length - 1];

      // Create display dates for the chart
      const startDisplayDate = new Date(startDate);
      const endDisplayDate = new Date(endDate);
      const displayStart = `${startDisplayDate.toLocaleString("en-US", { month: "short" })} ${startDisplayDate.getFullYear()}`;
      const displayEnd = `${endDisplayDate.toLocaleString("en-US", { month: "short" })} ${endDisplayDate.getFullYear()}`;

      // Transform to the expected format
      return {
        status: "complete",
        sql: data?.time_series_sql || "",
        chart: {
          data: formattedData,
          startDate,
          endDate,
          displayStart,
          displayEnd,
          total: data?.overall_value || 0,
          unit: unit || "",
        },
      };
    },
    reloadChart: function () {
      if (!endpoint) return;

      const filtersQueryString = objectToQueryString({ ...filters });
      this.loading = true;
      // Add a snapshot_results param to avoid saving the filters to the session
      const fullEndpoint = `${endpoint}${filters.chart_name ? "/bar_chart" : ""}${
        "?" +
        filtersQueryString +
        (this.variant ? "&variant=" + this.variant : "")
      }&snapshot_results=true`;

      fetchWithPolling(fullEndpoint, (resp) => !resp?.wait)
        .then((response) => {
          chartData.elChart = document.getElementById(
            `linechart-snapshot-results-${chartIndex}`,
          );
          chartData.elChart.innerHTML = null;

          chart = new window.LineChart(chartData);

          let data;
          if (filters.chart_name) {
            data = response.data;
          } else if (response.time_series) {
            // Handle new API format
            data = this.formatForNewApiData(response);
          } else {
            data = response;
          }

          this.chartData = data.chart;

          if (this.unit) {
            const formatIfNeeded = (value) => {
              if (this.unit.trim() === "minutes") {
                const result = formatTime(value);
                return {
                  value: result.value,
                  unit: ` ${result.unit}`,
                };
              }
              if (this.unit.trim() === "%") {
                return {
                  value: value && value?.toFixed(2),
                  unit: this.unit,
                };
              }
              return {
                value: value,
                unit: this.unit,
              };
            };

            const formattedTotal = formatIfNeeded(this.chartData.total);

            this.chartData = {
              ...this.chartData,
              total: formattedTotal.value,
              unit: formattedTotal.unit,
              data: this.chartData?.data?.map((d) => {
                const formattedData = formatIfNeeded(d.value);
                return {
                  ...d,
                  displayValue: formattedData.value,
                  unit: formattedData.unit,
                };
              }),
            };
          }

          this.sqlModalChartTitle = this.chartData.title;
          this.rawSql = data.sql;
        })
        .then(() => this.loadChart())
        .catch((error) => {
          console.error(`error for ${name} embedded datacloud chart:`, error);
          this.displayable = false;
          this.loading = false;
        });

      // This function is invoked in the DOM with display logic. Always return true
      return true;
    },
    loadChart: function () {
      // Avoids using this.chartData in the tooltip function
      const chartData = this.chartData;

      chart.yAxisMin = 0;
      chart.yAxisMax = 100;
      chart.values = chartData.data;
      this.displayable = chart.values
        ? chart.values.some((v) => v.value > 0)
        : false;
      chart.tooltipHtml = function (d) {
        const params = {
          heading: name,
          d: {
            label: d.label,
            formattedValue: `${d.displayValue}${d.unit}`,
          },
        };

        return chartTooltipHtml.default(params);
      };
      chart.startDate = chartData.startDate;
      chart.endDate = chartData.endDate;
      chart.yAxisMax = Math.max(...chartData.data.map((v) => v.value)) * 1.2;
      chart.redraw();
      this.loading = false;
    },
  };
}
